import React, { useState, useEffect } from 'react';

import { authService } from '../../services/authService';
import { barberService } from '../../services/barberService';
import { Barber, ProfileUpdateRequest, PasswordChangeRequest } from '../../types/barber.types';
import { useToast } from '../../context/ToastContext';
import BarberHeader from '../../components/barber/BarberHeader';
import { generateBarberSlug } from '../../utils/slugUtils';

type ViewMode = 'view' | 'edit';

const BarberProfile: React.FC = () => {
    const [barber, setBarber] = useState<Barber | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('view');
    const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
    const { showToast } = useToast();

    // Profile form
    const [profileForm, setProfileForm] = useState<ProfileUpdateRequest>({
        address: '',
        whatsapp: '',
        instagramUrl: '',
        facebookUrl: '',
    });

    // Password form
    const [passwordForm, setPasswordForm] = useState<PasswordChangeRequest>({
        currentPassword: '',
        newPassword: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');

    // Image preview
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);

    const user = authService.getCurrentUser();

    useEffect(() => {
        loadBarberProfile();
    }, []);

    const loadBarberProfile = async () => {
        try {
            setLoading(true);
            const barberData = await barberService.getMyStatus();
            setBarber(barberData);
            setProfileForm({
                address: barberData.address || '',
                whatsapp: barberData.whatsapp || '',
                instagramUrl: barberData.instagramUrl || '',
                facebookUrl: barberData.facebookUrl || '',
            });
            if (barberData.profilePicture) {
                setImagePreview(barberData.profilePicture);
            }
        } catch (error) {
            console.error('Error loading barber profile:', error);
            showToast('Error al cargar el perfil', 'error');
        } finally {
            setLoading(false);
        }
    };

    const buildImageUrl = (url?: string): string => {
        if (!url) return '';
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        return `${window.location.protocol}//${window.location.hostname}:8080${url}`;
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        try {
            let updatedForm = { ...profileForm };

            if (selectedImage) {
                setUploadingImage(true);
                const uploadResponse = await barberService.uploadProfileImage(selectedImage);
                updatedForm.profilePictureUrl = uploadResponse.url;
                setUploadingImage(false);
            }

            const updatedBarber = await barberService.updateProfile(updatedForm);
            setBarber(updatedBarber);
            setSelectedImage(null);
            setViewMode('view');
            showToast('Perfil actualizado correctamente', 'success');
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast('Error al actualizar el perfil', 'error');
        } finally {
            setSaving(false);
            setUploadingImage(false);
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordForm.newPassword !== confirmPassword) {
            showToast('Las contraseñas no coinciden', 'error');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            showToast('La contraseña debe tener al menos 6 caracteres', 'error');
            return;
        }

        console.log('Password change request:', {
            currentPassword: passwordForm.currentPassword ? '***' : 'EMPTY',
            newPassword: passwordForm.newPassword ? '***' : 'EMPTY',
            currentLength: passwordForm.currentPassword.length,
            newLength: passwordForm.newPassword.length
        });

        setSaving(true);

        try {
            await barberService.changePassword(passwordForm);
            setPasswordForm({ currentPassword: '', newPassword: '' });
            setConfirmPassword('');
            showToast('Contraseña cambiada correctamente', 'success');
        } catch (error: any) {
            console.error('Error changing password:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage = error.response?.data?.message || error.response?.data || 'Error al cambiar la contraseña. Verifica tu contraseña actual.';
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const cancelEdit = () => {
        setViewMode('view');
        setSelectedImage(null);
        setImagePreview(barber?.profilePicture || null);
        setProfileForm({
            address: barber?.address || '',
            whatsapp: barber?.whatsapp || '',
            instagramUrl: barber?.instagramUrl || '',
            facebookUrl: barber?.facebookUrl || '',
        });
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
                <div className="spinner"></div>
                <p>Cargando perfil...</p>
            </div>
        );
    }

    return (
        <>
            <BarberHeader
                title="👤 Mi Perfil"
                showBackButton={true}
                backPath="/barber/dashboard"
                rightContent={
                    viewMode === 'view' && activeTab === 'profile' ? (
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setViewMode('edit')}
                        >
                            ✏️ Editar
                        </button>
                    ) : viewMode === 'edit' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={cancelEdit}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleProfileSubmit}
                                disabled={saving || uploadingImage}
                            >
                                {uploadingImage ? '📤...' : saving ? '💾...' : '💾 Guardar'}
                            </button>
                        </div>
                    ) : null
                }
            />
            <div className="container mx-auto p-md md:p-xl fade-in" style={{ maxWidth: '1200px' }}>

                {/* Main Card */}
                <div className="card p-md md:p-xl" style={{ borderRadius: '1rem' }}>
                    {/* Profile Header */}
                    <div className="flex gap-lg mb-xl" style={{
                        paddingBottom: '2rem',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        {/* Avatar */}
                        <div className="flex flex-col items-center">
                            <div
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    minWidth: '120px',
                                    minHeight: '120px',
                                    borderRadius: '50%',
                                    backgroundColor: imagePreview ? 'transparent' : 'var(--background-dark)',
                                    color: imagePreview ? 'inherit' : 'var(--accent)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '3rem',
                                    overflow: 'hidden',
                                    border: '4px solid #e5e7eb',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    marginBottom: '1rem'
                                }}>
                                {imagePreview ? (
                                    <img
                                        src={buildImageUrl(imagePreview)}
                                        alt={`${barber?.firstName || ''} ${barber?.lastName || ''}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '50%'
                                        }}
                                    />
                                ) : (
                                    `${barber?.firstName?.charAt(0) || barber?.nickname?.charAt(0) || '?'}${barber?.lastName?.charAt(0) || ''}`
                                )}
                            </div>
                            <span className={`status-badge ${barber?.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                                {barber?.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}
                            </span>
                        </div>

                        {/* Basic Info */}
                        <div className="flex-1">
                            <p className="text-accent font-bold text-xl mb-xs">"{barber?.nickname}"</p>
                            <h2 className="text-primary font-bold mb-sm" style={{ fontSize: '1.75rem', margin: 0 }}>
                                {barber?.firstName || ''} {barber?.lastName || ''}
                            </h2>
                            <p className="text-secondary mb-md">{barber?.email || user?.email}</p>

                            <div className="flex gap-md flex-wrap">
                                {barber?.whatsapp && (
                                    <div className="flex items-center gap-xs">
                                        <span style={{ fontSize: '1.25rem' }}>📱</span>
                                        <span>{barber.whatsapp}</span>
                                    </div>
                                )}
                                {barber?.specialization && (
                                    <div className="flex items-center gap-xs">
                                        <span style={{ fontSize: '1.25rem' }}>✂️</span>
                                        <span>{barber.specialization}</span>
                                    </div>
                                )}
                                {barber?.yearsOfExperience && (
                                    <div className="flex items-center gap-xs">
                                        <span style={{ fontSize: '1.25rem' }}>🏆</span>
                                        <span>{barber.yearsOfExperience} años de experiencia</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="mb-lg">
                        <div style={{ borderBottom: '2px solid #e5e7eb', display: 'flex', gap: '2rem' }}>
                            <button
                                onClick={() => {
                                    setActiveTab('profile');
                                    setViewMode('view');
                                }}
                                style={{
                                    padding: '0.75rem 1rem',
                                    border: 'none',
                                    background: 'none',
                                    fontWeight: '600',
                                    borderBottom: activeTab === 'profile' ? '3px solid var(--accent)' : '3px solid transparent',
                                    color: activeTab === 'profile' ? 'var(--text-primary)' : 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    marginBottom: '-2px'
                                }}
                            >
                                👤 Información Personal
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('password');
                                    setViewMode('view');
                                }}
                                style={{
                                    padding: '0.75rem 1rem',
                                    border: 'none',
                                    background: 'none',
                                    fontWeight: '600',
                                    borderBottom: activeTab === 'password' ? '3px solid var(--accent)' : '3px solid transparent',
                                    color: activeTab === 'password' ? 'var(--text-primary)' : 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    marginBottom: '-2px'
                                }}
                            >
                                🔒 Cambiar Contraseña
                            </button>
                        </div>
                    </div>

                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <>
                            {viewMode === 'view' ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                                    {/* Link de Reserva */}
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <h3 className="text-primary font-bold mb-md" style={{ fontSize: '1.25rem' }}>
                                            🔗 Mi Enlace de Reservas
                                        </h3>
                                        <div className="p-md rounded-lg" style={{ background: 'var(--background-dark)', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                            <p className="text-sm flex-1 mb-0 font-mono bg-white p-sm rounded border text-secondary overflow-hidden text-ellipsis" style={{ whiteSpace: 'nowrap' }}>
                                                {window.location.origin}/book/{barber ? generateBarberSlug(barber) : ''}
                                            </p>
                                            <button 
                                                className="btn btn-primary btn-sm"
                                                onClick={() => {
                                                    if (barber) {
                                                        navigator.clipboard.writeText(`${window.location.origin}/book/${generateBarberSlug(barber)}`);
                                                        showToast('Enlace copiado al portapapeles', 'success');
                                                    }
                                                }}
                                            >
                                                📋 Copiar Enlace
                                            </button>
                                        </div>
                                        <p className="text-xs text-secondary mt-xs">
                                            Comparte este enlace con tus clientes. Serán redirigidos directamente a tu agenda para reservar.
                                        </p>
                                    </div>

                                    {/* Contact Information */}
                                    <div>
                                        <h3 className="text-primary font-bold mb-md" style={{ fontSize: 'clamp(1.125rem, 3vw, 1.25rem)' }}>
                                            📞 Información de Contacto
                                        </h3>
                                        <div className="space-y-sm">
                                            {barber?.address ? (
                                                <div className="p-md rounded-lg" style={{ background: 'var(--background-dark)' }}>
                                                    <p className="text-sm text-secondary mb-xs">Dirección</p>
                                                    <p className="font-semibold">{barber.address}</p>
                                                </div>
                                            ) : (
                                                <p className="text-secondary italic">No hay dirección configurada</p>
                                            )}
                                            {barber?.whatsapp && (
                                                <div className="p-md rounded-lg" style={{ background: 'var(--background-dark)' }}>
                                                    <p className="text-sm text-secondary mb-xs">WhatsApp</p>
                                                    <p className="font-semibold">{barber.whatsapp}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Social Media */}
                                    <div>
                                        <h3 className="text-primary font-bold mb-md" style={{ fontSize: '1.25rem' }}>
                                            🌐 Redes Sociales
                                        </h3>
                                        <div className="space-y-sm">
                                            {barber?.instagramUrl && (
                                                <div className="p-md rounded-lg" style={{ background: 'var(--background-dark)' }}>
                                                    <p className="text-sm text-secondary mb-xs">Instagram</p>
                                                    <a href={barber.instagramUrl} target="_blank" rel="noopener noreferrer"
                                                        className="text-primary hover:underline font-semibold">
                                                        Ver perfil →
                                                    </a>
                                                </div>
                                            )}
                                            {barber?.facebookUrl && (
                                                <div className="p-md rounded-lg" style={{ background: 'var(--background-dark)' }}>
                                                    <p className="text-sm text-secondary mb-xs">Facebook</p>
                                                    <a href={barber.facebookUrl} target="_blank" rel="noopener noreferrer"
                                                        className="text-primary hover:underline font-semibold">
                                                        Ver perfil →
                                                    </a>
                                                </div>
                                            )}
                                            {!barber?.instagramUrl && !barber?.facebookUrl && (
                                                <p className="text-secondary italic">No hay redes sociales configuradas</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    <div>
                                        <label className="form-label">📷 Foto de Perfil</label>
                                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <input
                                                type="file"
                                                id="profile-image"
                                                accept="image/*"
                                                onChange={handleImageSelect}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="profile-image" className="btn btn-outline" style={{ cursor: 'pointer' }}>
                                                {selectedImage ? '✓ Imagen seleccionada' : '📸 Seleccionar imagen'}
                                            </label>
                                            {selectedImage && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline"
                                                    onClick={() => {
                                                        setSelectedImage(null);
                                                        setImagePreview(barber?.profilePicture || null);
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="form-label">📍 Dirección</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Ej: Av. Corrientes 1234, CABA"
                                            value={profileForm.address}
                                            onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label">📱 WhatsApp</label>
                                        <input
                                            type="tel"
                                            className="form-input"
                                            placeholder="Ej: +54 9 11 1234-5678"
                                            value={profileForm.whatsapp}
                                            onChange={(e) => setProfileForm({ ...profileForm, whatsapp: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label">📸 Instagram URL</label>
                                        <input
                                            type="url"
                                            className="form-input"
                                            placeholder="https://instagram.com/nombre-usuario"
                                            value={profileForm.instagramUrl}
                                            onChange={(e) => setProfileForm({ ...profileForm, instagramUrl: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="form-label">🔵 Facebook URL</label>
                                        <input
                                            type="url"
                                            className="form-input"
                                            placeholder="https://facebook.com/nombre-usuario"
                                            value={profileForm.facebookUrl}
                                            onChange={(e) => setProfileForm({ ...profileForm, facebookUrl: e.target.value })}
                                        />
                                    </div>
                                </form>
                            )}
                        </>
                    )}

                    {/* Password Tab */}
                    {activeTab === 'password' && (
                        <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '600px' }}>
                            <div>
                                <label className="form-label">🔒 Contraseña Actual</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    placeholder="Ingresa tu contraseña actual"
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                    required
                                />
                            </div>

                            <div>
                                <label className="form-label">🔑 Nueva Contraseña</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    placeholder="Mínimo 6 caracteres"
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                    required
                                    minLength={6}
                                />
                            </div>

                            <div>
                                <label className="form-label">🔑 Confirmar Nueva Contraseña</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    placeholder="Repite la nueva contraseña"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={6}
                                />
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? '🔄 Cambiando...' : '🔒 Cambiar Contraseña'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div >
        </>
    );
};

export default BarberProfile;
